declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>
  }
}

// Track which screen views have been logged to prevent duplicates (e.g., from React StrictMode)
const trackedScreenViews = new Set<string>()

// Track step views with timestamps to prevent duplicates within a short window (e.g., from React StrictMode)
// Allows re-tracking if user navigates back to a step after some time
const trackedStepViews = new Map<string, number>()
const STEP_VIEW_DEDUPE_WINDOW_MS = 1000 // 1 second window to prevent StrictMode duplicates

/**
 * Initialize dataLayer if it doesn't exist
 * GTM typically initializes this, but we ensure it exists as a fallback
 */
function ensureDataLayer(): void {
  if (typeof window === 'undefined') return
  
  if (!window.dataLayer) {
    window.dataLayer = []
  }
}

/**
 * Track a custom event to Google Tag Manager dataLayer
 * Events are automatically picked up by GTM and forwarded to GA4 if configured
 */
export function trackEvent(eventName: string, params: Record<string, unknown>) {
  if (typeof window === 'undefined') return

  // Ensure dataLayer exists
  ensureDataLayer()

  // Debug logging in development
  if (import.meta.env.DEV) {
    console.log('[Analytics] trackEvent called:', eventName, params)
  }

  // Push event to dataLayer (GTM will handle forwarding to GA4)
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...params,
      timestamp: new Date().toISOString(),
    })

    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('[Analytics] Event pushed to dataLayer:', eventName)
    }
  } else {
    console.warn('[Analytics] dataLayer not available. Event not tracked:', eventName)
  }
}

export function trackWizardStarted(consultationId: string) {
  trackEvent('wizard_started', {
    consultation_id: consultationId,
  })
}

export function trackStepView(stepNumber: number, questionId: string) {
  // Create a unique key for this step view to prevent duplicates
  const trackingKey = `step-${stepNumber}-${questionId}`
  const now = Date.now()
  
  // Check if this step was recently tracked (within dedupe window)
  const lastTracked = trackedStepViews.get(trackingKey)
  if (lastTracked && (now - lastTracked) < STEP_VIEW_DEDUPE_WINDOW_MS) {
    if (import.meta.env.DEV) {
      console.log('[Analytics] Step view already tracked recently, skipping:', stepNumber, questionId)
    }
    return
  }
  
  // Update tracking timestamp
  trackedStepViews.set(trackingKey, now)
  trackEvent('step_view', {
    step_number: stepNumber,
    question_id: questionId,
  })
}

export function trackWizardCompleted(consultationId: string, totalSteps: number) {
  trackEvent('wizard_completed', {
    consultation_id: consultationId,
    total_steps: totalSteps,
  })
}

export function trackLanguageChanged(fromLanguage: string, toLanguage: string, currentStep?: number) {
  trackEvent('language_changed', {
    from_language: fromLanguage,
    to_language: toLanguage,
    ...(currentStep !== undefined && { current_step: currentStep }),
  })
}

export function trackConversion(consultationId: string) {
  trackEvent('consultation_conversion', {
    consultation_id: consultationId,
  })
}

export function trackScreenView(screenName: string, consultationId: string, funnelPosition: number) {
  // Create a unique key for this screen view to prevent duplicates
  const trackingKey = `${screenName}-${consultationId}-${funnelPosition}`
  
  // Skip if already tracked (prevents duplicates from React StrictMode)
  if (trackedScreenViews.has(trackingKey)) {
    if (import.meta.env.DEV) {
      console.log('[Analytics] Screen view already tracked, skipping:', screenName)
    }
    return
  }
  
  trackedScreenViews.add(trackingKey)
  trackEvent('screen_view', {
    screen_name: screenName,
    consultation_id: consultationId,
    funnel_position: funnelPosition,
  })
}

export function trackWelcomeOptionSelected(option: string, consultationId: string) {
  trackEvent('welcome_option_selected', {
    option: option,
    consultation_id: consultationId,
  })
}
